import type { ProductRelation, ProductRelationType, ProductRelations } from '../../types'
import { canonicalPath } from './seo'

/**
 * Renders the STORY-003 product relations grouped by type. Dutch labels
 * (CLAUDE.md rule) — if a group is empty, it's skipped. Unresolved
 * targets (no `resolved` projection) render as disabled tiles so a
 * customer still sees the MPN referenced on the B&R side without a
 * broken link.
 */

const TYPE_ORDER: ProductRelationType[] = [
  'replaced_by',
  'replaces',
  'compatible_with',
  'related',
]

const TYPE_LABELS: Record<ProductRelationType, string> = {
  replaces: 'Vervangt',
  replaced_by: 'Vervangen door',
  compatible_with: 'Compatibel met',
  related: 'Gerelateerd',
}

export default function ProductRelationsSection({
  lang,
  relations,
}: {
  lang: string
  relations: ProductRelations | undefined
}) {
  if (!relations) return null

  const nonEmpty = TYPE_ORDER.filter((t) => (relations[t]?.length ?? 0) > 0)
  if (nonEmpty.length === 0) return null

  return (
    <section className="mt-10" aria-labelledby="product-relations-heading">
      <h2 id="product-relations-heading" className="text-xl font-semibold text-neutral-900 mb-4">
        Gerelateerde producten
      </h2>
      <div className="flex flex-col gap-8">
        {nonEmpty.map((type) => (
          <RelationGroup key={type} lang={lang} type={type} items={relations[type]} />
        ))}
      </div>
    </section>
  )
}

function RelationGroup({
  lang,
  type,
  items,
}: {
  lang: string
  type: ProductRelationType
  items: ProductRelation[]
}) {
  return (
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-600 mb-2">
        {TYPE_LABELS[type]}
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((rel) => (
          <RelationTile key={rel.target_mpn} lang={lang} rel={rel} />
        ))}
      </div>
    </div>
  )
}

function RelationTile({ lang, rel }: { lang: string; rel: ProductRelation }) {
  if (rel.resolved === null) {
    return (
      <div
        className="block rounded-lg border border-dashed border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-600"
        title="Dit gerelateerde product staat nog niet in onze catalogus"
      >
        <div className="font-mono text-xs text-neutral-600">{rel.target_mpn}</div>
        <div className="mt-1 text-neutral-600">Nog niet in catalogus</div>
      </div>
    )
  }
  const href = canonicalPath(lang, rel.resolved.slug)
  return (
    <a
      href={href}
      className="block rounded-lg border border-neutral-200 bg-white p-3 hover:border-primary/30 hover:shadow-sm transition-all"
    >
      <div className="aspect-[4/3] overflow-hidden rounded-md bg-white flex items-center justify-center p-2">
        <img
          src={rel.resolved.primary_image_url ?? '/placeholder-product.svg'}
          alt={rel.resolved.display_title}
          className="max-h-full max-w-full object-contain"
        />
      </div>
      <div className="mt-2 font-mono text-xs text-neutral-600">
        {rel.resolved.normalized_mpn}
      </div>
      <div className="text-sm text-neutral-900 line-clamp-2">{rel.resolved.display_title}</div>
      {migrationNote(lang, rel) && (
        <div className="mt-1 text-xs text-amber-700">{migrationNote(lang, rel)}</div>
      )}
    </a>
  )
}

/** Pick the migration note in the active language (T4.2), nullable. */
function migrationNote(lang: string, rel: ProductRelation): string | null {
  const n = rel.migration_note
  if (!n) return null
  return (lang === 'en' ? n.en : n.nl) ?? n.nl ?? n.en ?? null
}
